import {useMutation} from "@apollo/client";
import identity from "lodash/identity";
import isEmpty from "lodash/isEmpty";
import pickBy from "lodash/pickBy";
import {Suspense, useEffect} from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {GQL_CREDENTIAL_UPDATE, GQL_CREDENTIALS_QUERY} from "@/lib/queries/credentials.ts";
import {ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {CredentialUpdateResponse} from "@/lib/types/credentials";
import {handleFormatingCertificateFiles} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

import {CredentialUpdateBody} from "./CredentialUpdateBody.tsx";
import {UpdateCredentialFormValues} from "./CredentialUpdateForm.tsx";

interface CredentialUpdateModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  credentialId?: string;
}

export const CredentialUpdateModal = ({show, setShow, credentialId}: CredentialUpdateModalProps) => {
  const formId = "update-credential-form";
  const {user} = useCurrentUserContext();
  const [updateCredential, {data, loading: mutationLoading, error: mutationError, reset}] =
    useMutation<CredentialUpdateResponse>(GQL_CREDENTIAL_UPDATE, {
      refetchQueries: [GQL_CREDENTIALS_QUERY],
    });

  let errorMessage: ServerErrors = {__all__: []};
  if (data?.response?.status === ResponseStatus.ERROR) {
    errorMessage = {...errorMessage, ...data?.response?.message};
  }
  if (mutationError) {
    errorMessage.__all__.push("something went wrong please contact support");
  }

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const handleSubmit = async (data: UpdateCredentialFormValues) => {
    data.secret = pickBy(data.secret, identity);

    await handleFormatingCertificateFiles(data);

    if ("secret" in data && isEmpty(data.secret)) {
      delete data.secret;
    }
    if ("username" in data && data.username === "") {
      delete data.username;
    }

    const response = await updateCredential({
      variables: {
        id: credentialId,
        ...data,
      },
    });
    if (response?.data?.response?.status === ResponseStatus.SUCCESS) {
      setShow(false);
    }
  };

  return (
    <FormModal
      show={show}
      onCancel={() => setShow(false)}
      formId={formId}
      hasRequiredFields
      disabled={mutationLoading}
      hasSubmitButton={user?.has_write_access}
      title="Edit Vault Item"
      size="lg"
      centered
    >
      <UpErrorBoundary>
        <Suspense>
          <CredentialUpdateBody
            credentialId={credentialId}
            formId={formId}
            onSubmit={handleSubmit}
            serverErrors={errorMessage}
          />
        </Suspense>
      </UpErrorBoundary>
    </FormModal>
  );
};
